'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Grid, GridItem, Box, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBallotCheck,
  faLayerGroup,
  faToolbox,
  faCalendarDays,
  faCartShopping,
  faClipboardListCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { setStateValue } from '@/app/redux/slices/configGymSlice';

export default function ProgressNav() {
  const router = useRouter();
  const dispatch = useDispatch();

  const progress = useSelector((state) => state.gym.progressOpt);
  const service = useSelector((state) => state.gym.serviceOpt);
  const survey = useSelector((state) => state.gym.survey);
  const isCheckoutValid = useSelector((state) => state.gym.isCheckoutValid);
  const isValid = useSelector((state) => state.gym.isValid);
  const isMobile = useSelector((state) => state.gym.isMobile);
  // State for derived values
  const [surveyCompleted, setSurveyCompleted] = useState(false);

  // Update `surveyCompleted` dynamically
  useEffect(() => {
    setSurveyCompleted(survey.some((item) => item.complete));
  }, [survey]);

  // Handle redirects based on progress
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');

    if (redirectStatus === 'succeeded') {
      router.push('/thankyou');
      return;
    }

    const routeMap = {
      Service: '/gymServices',
      Mirrors: '/mirrors',
      Fitting: '/fitting',
      Survey: '/survey',
      Booking: surveyCompleted ? '/booking' : null,
      Payment: isCheckoutValid ? '/summary' : null,
    };

    if (routeMap[progress]) {
      router.push(routeMap[progress]);
    } else if (progress === 'Booking' && !surveyCompleted) {
      console.log('Cannot go to booking: ', surveyCompleted);
    } else if (progress === 'Payment' && !isCheckoutValid) {
      console.log('Cannot go to checkout: ', isCheckoutValid);
    }
  }, [progress, surveyCompleted, isCheckoutValid]);

  // Columns for navigation
  const columns = [
    { name: 'Service', icon: faBallotCheck },
    { name: 'Mirrors', icon: faLayerGroup },
    { name: 'Fitting', icon: faToolbox },
    { name: 'Survey', icon: faClipboardListCheck },
    { name: 'Booking', icon: faCalendarDays },
    { name: 'Payment', icon: faCartShopping },
  ];

  // Filter logic for visibility
  const filteredColumns = columns.filter((column) => {
    if (
      service === 'survey' &&
      (column.name === 'Mirrors' || column.name === 'Fitting')
    ) {
      return false;
    }
    if (
      service === 'basic' &&
      (column.name === 'Survey' || column.name === 'Booking')
    ) {
      return false;
    }
    return true;
  });

  // Style objects
  const progressChoice = {
    color: '#00000050',
    fontWeight: '300',
    borderBottom: '3px solid #00000020',
    _hover: {
      fontWeight: '700',
      color: '#0cc6de',
      borderBottom: '3px solid #0cc6de',
      cursor: 'pointer',
    },
  };

  const progressSelected = {
    color: '#000000',
    fontWeight: '700',
    borderBottom: '3px solid #000000',
  };

  // Handle selection
  const handleSelect = (name) => {
    dispatch(setStateValue({ key: 'progressOpt', value: name }));
  };

  return (
    <Grid
      templateColumns={`repeat(${filteredColumns.length}, 1fr)`}
      gap={0}
      p={0}
      width="100%"
      minHeight="100%"
    >
      {filteredColumns.map((column, index) => {
        // Define the disabled state for each column
        const isDisabled =
          (column.name === 'Booking' && !surveyCompleted) ||
          (column.name === 'Payment' && !isCheckoutValid);

        return (
          <GridItem key={index} p={0} fontSize={{ base: '4vw', md: '1vw' }}>
            <Box
              {...(progress === column.name
                ? progressSelected
                : progressChoice)}
              display="flex"
              alignItems="center"
              justifyContent="center"
              padding="0 0 0.5vw 1vw"
              height={{ base: '8vw', md: '2vw' }}
              onClick={!isDisabled ? () => handleSelect(column.name) : null} // Disable onClick
              cursor={!isDisabled ? 'pointer' : 'not-allowed'} // Change cursor style
              opacity={!isDisabled ? 1 : 0.5} // Reduce opacity when disabled
            >
              <FontAwesomeIcon
                icon={column.icon}
                style={{
                  marginRight: progress === column.name ? '1.2vw' : '0',
                }}
              />
              {(!isMobile || progress === column.name) && (
                <Text ml="1vw" transition="opacity 0.3s ease-in-out">
                  {column.name}
                </Text>
              )}
            </Box>
          </GridItem>
        );
      })}
    </Grid>
  );
}
